import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Avatar,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import {
  TrendingUp,
  Person,
  LocalHospital,
  Refresh,
  FilterList,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { ipdDoneService, IPD_FILTER_OPTIONS } from '../../services/ipdDone';
import LoadingSpinner from '../common/LoadingSpinner';

const IPDStatistics = ({ onError }) => {
  const [loading, setLoading] = useState(false);
  const [statisticsData, setStatisticsData] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [filters, setFilters] = useState({
    period: 'monthly',
    name: '',
    role_filter: 'all',
    status_filter: 'all',
    min_ipd_count: '',
    min_conversion_rate: '',
    sort_by: 'ipd_count',
    sort_order: 'desc',
    start_date: null,
    end_date: null,
  });

  useEffect(() => {
    loadStatistics();
  }, [page, pageSize]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: page + 1,
        page_size: pageSize,
      };

      // Format dates
      if (filters.start_date) {
        params.start_date = format(filters.start_date, 'yyyy-MM-dd');
      }
      if (filters.end_date) {
        params.end_date = format(filters.end_date, 'yyyy-MM-dd');
      }

      const data = await ipdDoneService.getIPDStatistics(params);
      setStatisticsData(data);
    } catch (error) {
      console.error('Failed to load IPD statistics:', error);
      onError('Failed to load IPD statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setPage(0); // Reset to first page
    loadStatistics();
  };

  const handleClearFilters = () => {
    setFilters({
      period: 'monthly',
      name: '',
      role_filter: 'all',
      status_filter: 'all',
      min_ipd_count: '',
      min_conversion_rate: '',
      sort_by: 'ipd_count',
      sort_order: 'desc',
      start_date: null,
      end_date: null,
    });
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  const formatPercentage = (num) => {
    return `${num?.toFixed(1) || '0.0'}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, HH:mm');
    } catch {
      return 'Invalid Date';
    }
  };

  if (loading && !statisticsData) {
    return <LoadingSpinner message="Loading IPD statistics..." />;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">IPD Done Statistics</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            endIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadStatistics}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Filters Panel */}
      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Filter Options
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Period</InputLabel>
                  <Select
                    value={filters.period}
                    onChange={(e) => handleFilterChange('period', e.target.value)}
                    label="Period"
                  >
                    {IPD_FILTER_OPTIONS.periods.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Name Search"
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                  placeholder="Search by name..."
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={filters.status_filter}
                    onChange={(e) => handleFilterChange('status_filter', e.target.value)}
                    label="Status Filter"
                  >
                    {IPD_FILTER_OPTIONS.statusFilters.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Role Filter</InputLabel>
                  <Select
                    value={filters.role_filter}
                    onChange={(e) => handleFilterChange('role_filter', e.target.value)}
                    label="Role Filter"
                  >
                    {IPD_FILTER_OPTIONS.roleFilters.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Min IPD Count"
                  value={filters.min_ipd_count}
                  onChange={(e) => handleFilterChange('min_ipd_count', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Min Conversion Rate"
                  value={filters.min_conversion_rate}
                  onChange={(e) => handleFilterChange('min_conversion_rate', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={filters.sort_by}
                    onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                    label="Sort By"
                  >
                    {IPD_FILTER_OPTIONS.sortOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort Order</InputLabel>
                  <Select
                    value={filters.sort_order}
                    onChange={(e) => handleFilterChange('sort_order', e.target.value)}
                    label="Sort Order"
                  >
                    <MenuItem value="desc">Descending</MenuItem>
                    <MenuItem value="asc">Ascending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                disabled={loading}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                disabled={loading}
              >
                Clear All
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Overall Statistics */}
      {statisticsData?.overall_stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalHospital color="success" />
                  <Typography variant="h6">Total IPD Done</Typography>
                </Box>
                <Typography variant="h4" color="success.main">
                  {formatNumber(statisticsData.overall_stats.total_ipd_done)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalHospital color="warning" />
                  <Typography variant="h6">Pending IPD</Typography>
                </Box>
                <Typography variant="h4" color="warning.main">
                  {formatNumber(statisticsData.overall_stats.total_pending_ipd)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person color="primary" />
                  <Typography variant="h6">Team Members</Typography>
                </Box>
                <Typography variant="h4" color="primary.main">
                  {formatNumber(statisticsData.overall_stats.filtered_team_members_count)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp color="info" />
                  <Typography variant="h6">Avg Conversion</Typography>
                </Box>
                <Typography variant="h4" color="info.main">
                  {formatPercentage(statisticsData.overall_stats.filtered_stats?.avg_conversion_rate)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Team Performance Table */}
      {statisticsData?.team_ipd_performance && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Team IPD Performance
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Team Member</TableCell>
                    <TableCell align="center">Role</TableCell>
                    <TableCell align="center">IPD Done</TableCell>
                    <TableCell align="center">Pending</TableCell>
                    <TableCell align="center">Total Leads</TableCell>
                    <TableCell align="center">Conversion Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statisticsData.team_ipd_performance.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {member.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {member.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {member.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={member.role.toUpperCase()}
                          size="small"
                          color={member.role === 'bda' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="success.main" fontWeight="medium">
                          {formatNumber(member.ipd_done_count)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="warning.main" fontWeight="medium">
                          {formatNumber(member.pending_ipd_requests)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {formatNumber(member.total_leads)}
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          color={member.ipd_conversion_rate > 10 ? 'success.main' : 'text.secondary'}
                          fontWeight="medium"
                        >
                          {formatPercentage(member.ipd_conversion_rate)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {statisticsData?.pagination && (
              <TablePagination
                component="div"
                count={statisticsData.pagination.total_results}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handlePageSizeChange}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default IPDStatistics;